/**
 * Cash & Bank transaction history: every movement in the period, searchable
 * and filterable, with where it came from, who, its reference and the account
 * it landed in or left.
 *
 * The rows are the balances' own ledger (GET /money/transactions), filtered on
 * the server, so a filtered list and its totals are always the server's
 * figures. A transfer between the company's own accounts is one row, from →
 * to, and is never money in or out. Nothing here posts anything.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { CASH_BANK_TRANSACTION_TYPES, type CashBankTransaction, type CashBankTransactionType } from '@cenox/shared';
import { Badge, Button, Card, EmptyState, ErrorState, Input, Modal, Select, TableSkeleton, cn } from '../../components/ui';
import { dateTime, money } from '../../lib/format';
import { useCashBankTransactions, useFetchAllTransactions } from './data';
import { SOURCE_LABELS } from './labels';

const PAGE = 50;

const LOAN_KIND_LABELS: Record<string, string> = {
  disbursement: 'Loan paid out',
  top_up: 'Loan top-up',
  repayment: 'Loan repayment',
};

/** What a row is called: the ledger, and for a loan what kind of entry. */
export function transactionLabel(r: Pick<CashBankTransaction, 'type' | 'subtype' | 'reversal' | 'amount'>): string {
  const base = r.type === 'loan' && r.subtype ? (LOAN_KIND_LABELS[r.subtype] ?? 'Loan') : (SOURCE_LABELS[r.type] ?? r.type);
  return r.reversal ? `Reversed: ${base.toLowerCase()}` : base;
}

/** Where the money went: into an account, out of one, or between two. */
export function destinationOf(r: CashBankTransaction): string {
  if (r.direction === 'transfer') return `${r.fromAccountName ?? '?'} → ${r.accountName ?? '?'}`;
  const where = r.accountName ?? `Not in an account (${r.kind === 'cash' ? 'cash' : 'bank'})`;
  return r.direction === 'out' ? `From ${where}` : where;
}

export function TransactionHistory({
  from,
  to,
  scope,
  currency,
  timezone,
  locations,
}: {
  from: string;
  to: string;
  /** The page's own filters: location area, account, worker. */
  scope: Record<string, string | undefined>;
  currency: string;
  timezone: string;
  locations: Array<{ id: string; name: string }>;
}): ReactNode {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [direction, setDirection] = useState('');
  const [kind, setKind] = useState('');
  const [branchId, setBranchId] = useState('');
  const [offset, setOffset] = useState(0);
  const [viewing, setViewing] = useState<CashBankTransaction | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const fetchAll = useFetchAllTransactions();

  const filters = useMemo(
    () => ({
      from,
      to,
      ...scope,
      ...(branchId ? { branchId } : {}),
      ...(type ? { types: type } : {}),
      ...(direction ? { direction } : {}),
      ...(kind ? { kind } : {}),
      ...(search.trim() ? { q: search.trim() } : {}),
    }),
    [from, to, scope, branchId, type, direction, kind, search],
  );
  const history = useCashBankTransactions({ ...filters, limit: PAGE, offset });
  const data = history.data;
  const m = (v: string) => money(v, currency);
  // Any filter change starts again from the first page.
  const reset = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setOffset(0);
  };

  async function exportCsv(): Promise<void> {
    setExporting(true);
    setExportError(null);
    try {
      const all = await fetchAll(filters);
      downloadCsv(`cash-bank-${from}-to-${to}.csv`, toCsv(all.rows, timezone));
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Could not export.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">Transaction history</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            {from} to {to}. Every payment in and out, from the same records as the balances. A transfer between your own
            accounts is one row and is never money in or out.
          </p>
        </div>
        <Button variant="secondary" size="sm" loading={exporting} disabled={!data || data.count === 0} onClick={() => void exportCsv()}>
          Export CSV
        </Button>
      </div>
      {exportError ? <p role="alert" className="mt-1 text-xs text-red-700">{exportError}</p> : null}

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="history-filters">
        <Input
          label="Search"
          value={search}
          onChange={(e) => reset(setSearch)(e.target.value)}
          placeholder="Name, reference, description"
        />
        <Select
          label="Type"
          value={type}
          onChange={(e) => reset(setType)(e.target.value)}
          options={[
            { value: '', label: 'All types' },
            ...CASH_BANK_TRANSACTION_TYPES.map((t: CashBankTransactionType) => ({ value: t, label: SOURCE_LABELS[t] ?? t })),
          ]}
        />
        <Select
          label="Direction"
          value={direction}
          onChange={(e) => reset(setDirection)(e.target.value)}
          options={[
            { value: '', label: 'In, out and transfers' },
            { value: 'in', label: 'Money in' },
            { value: 'out', label: 'Money out' },
            { value: 'transfer', label: 'Transfers between accounts' },
          ]}
        />
        <Select
          label="Cash or bank"
          value={kind}
          onChange={(e) => reset(setKind)(e.target.value)}
          options={[
            { value: '', label: 'Cash and bank' },
            { value: 'cash', label: 'Cash' },
            { value: 'bank', label: 'Bank' },
          ]}
        />
        <Select
          label="Location"
          value={branchId}
          onChange={(e) => reset(setBranchId)(e.target.value)}
          options={[{ value: '', label: 'All locations' }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
        />
      </div>

      {history.isError ? (
        <div className="mt-3">
          <ErrorState message="Could not load the transactions." onRetry={() => void history.refetch()} />
        </div>
      ) : !data ? (
        <div className="mt-3">
          <TableSkeleton rows={5} columns={8} />
        </div>
      ) : data.count === 0 ? (
        <div className="mt-3">
          <EmptyState title="No transactions" description="Nothing moved in these dates with these filters." />
        </div>
      ) : (
        <>
          <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="history-totals">
            <div className="flex gap-2">
              <dt className="text-slate-500">Money in</dt>
              <dd className="whitespace-nowrap tabular-nums text-emerald-700">{m(data.totals.moneyIn)}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-slate-500">Money out</dt>
              <dd className="whitespace-nowrap tabular-nums text-rose-700">{m(data.totals.moneyOut)}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-slate-500">Moved between accounts</dt>
              <dd className="whitespace-nowrap tabular-nums text-slate-700">{m(data.totals.transfers)}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-slate-500">Transactions</dt>
              <dd className="tabular-nums">{data.count}</dd>
            </div>
          </dl>
          <div className="-mx-3 mt-2 overflow-x-auto px-3">
            <table className="w-full min-w-[60rem] text-sm" data-testid="cash-bank-history">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3 font-medium">Date</th>
                  <th className="py-2 pr-3 font-medium">Type</th>
                  <th className="py-2 pr-3 font-medium">Source / description</th>
                  <th className="py-2 pr-3 font-medium">Person or rider</th>
                  <th className="py-2 pr-3 font-medium">Reference</th>
                  <th className="py-2 pr-3 text-right font-medium">Amount</th>
                  <th className="py-2 pr-3 font-medium">Account</th>
                  <th className="py-2 pr-3 font-medium">Location</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap py-1.5 pr-3 text-xs text-slate-600">
                      {r.on}
                      {r.at ? <span className="block text-slate-400">{timeOf(r.at, timezone)}</span> : null}
                    </td>
                    <td className="py-1.5 pr-3">
                      {transactionLabel(r)}
                      {r.status === 'pending' ? <Badge tone="warning">Waiting</Badge> : null}
                    </td>
                    <td className="max-w-[16rem] truncate py-1.5 pr-3 text-slate-600" title={r.description ?? undefined}>{r.description ?? '—'}</td>
                    <td className="py-1.5 pr-3">
                      {r.person ?? (r.rider ? null : '—')}
                      {r.rider && r.rider !== r.person ? (
                        <span className={cn('text-xs text-slate-500', r.person ? 'block' : '')}>Rider: {r.rider}</span>
                      ) : null}
                    </td>
                    <td className="py-1.5 pr-3 text-xs text-slate-600">{r.reference ?? '—'}</td>
                    <td
                      className={cn(
                        'whitespace-nowrap py-1.5 pr-3 text-right tabular-nums',
                        r.direction === 'transfer' ? 'text-slate-700' : Number(r.amount) < 0 ? 'text-rose-700' : 'text-emerald-700',
                      )}
                    >
                      {m(r.amount)}
                    </td>
                    <td className={cn('py-1.5 pr-3', r.accountId ? 'text-slate-600' : 'text-amber-700')}>{destinationOf(r)}</td>
                    <td className="py-1.5 pr-3 text-xs text-slate-500">{r.branchName ?? '—'}</td>
                    <td className="py-1.5 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setViewing(r)} aria-label={`View ${transactionLabel(r)} ${r.reference ?? r.on}`}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-500">
            <span>
              {offset + 1}–{Math.min(offset + PAGE, data.count)} of {data.count}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                Previous
              </Button>
              <Button variant="ghost" size="sm" disabled={offset + PAGE >= data.count} onClick={() => setOffset(offset + PAGE)}>
                Next
              </Button>
            </div>
          </div>
        </>
      )}
      {viewing ? <TransactionModal row={viewing} currency={currency} timezone={timezone} onClose={() => setViewing(null)} /> : null}
    </Card>
  );
}

function TransactionModal({ row, currency, timezone, onClose }: { row: CashBankTransaction; currency: string; timezone: string; onClose: () => void }): ReactNode {
  const fields: Array<[string, string | null]> = [
    ['Type', transactionLabel(row)],
    ['Date', row.on],
    ['Time', row.at ? dateTime(row.at, timezone) : null],
    ['Amount', money(row.amount, currency)],
    [
      'Direction',
      row.direction === 'transfer' ? 'Between your own accounts — not income, not an expense' : row.direction === 'in' ? 'Money in' : 'Money out',
    ],
    ['Description', row.description],
    ['Person', row.person],
    ['Rider', row.rider],
    ['Reference', row.reference],
    ['Method', row.method?.replace(/_/g, ' ') ?? null],
    ['Cash or bank', row.kind === 'cash' ? 'Cash' : 'Bank'],
    ...(row.direction === 'transfer'
      ? ([
          ['From', row.fromAccountName],
          ['To', row.accountName],
          ['Status', row.status === 'pending' ? 'Waiting for the receiver to confirm' : 'Arrived'],
        ] as Array<[string, string | null]>)
      : ([[row.direction === 'in' ? 'Into' : 'Out of', row.accountName ?? 'Not in an account yet']] as Array<[string, string | null]>)),
    ['Location', row.branchName],
    ['Recorded by', row.recordedBy],
    ['Note', row.note],
  ];
  return (
    <Modal open onClose={onClose} title={`${transactionLabel(row)} · ${money(row.amount, currency)}`}>
      <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1.5 text-sm" data-testid="transaction-details">
        {fields
          .filter(([, v]) => v !== null && v !== '')
          .map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-slate-500">{k}</dt>
              <dd className="break-words text-slate-900">{v}</dd>
            </div>
          ))}
      </dl>
      <div className="mt-4 flex justify-end">
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}

function timeOf(iso: string, timezone: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: timezone });
}

/** The rows as CSV, one line each, amounts as plain numbers. */
export function toCsv(rows: CashBankTransaction[], timezone: string): string {
  const header = ['Date', 'Time', 'Type', 'Description', 'Person', 'Rider', 'Reference', 'Amount', 'Direction', 'Cash or bank', 'From account', 'Account', 'Location', 'Recorded by', 'Status'];
  const cell = (v: string | null | undefined): string => {
    const s = v ?? '';
    // Quoted, and never read as a formula by a spreadsheet.
    const safe = /^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s) ? `'${s}` : s;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  const lines = rows.map((r) =>
    [
      r.on,
      r.at ? timeOf(r.at, timezone) : '',
      transactionLabel(r),
      r.description,
      r.person,
      r.rider,
      r.reference,
      r.amount,
      r.direction,
      r.kind,
      r.fromAccountName,
      r.accountName ?? 'Not in an account',
      r.branchName,
      r.recordedBy,
      r.status,
    ]
      .map(cell)
      .join(','),
  );
  return [header.map(cell).join(','), ...lines].join('\r\n');
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
